import { Throwable } from "@wss/error-tracking/throwable";
import { PlusPotentialSavingsInfo } from "../../../models";
import { ModalState } from "./modalState";
import PinterestApiEvent from "~/analytics/MarketingEvents/Pinterest/PinterestApiEvent";
import PinterestTagSignUpEvent from "~/analytics/TrackingPixels/TagEvents/PinterestTagSignUpEvent";
import { toError } from "@wss/error-tracking/utils";
import { logException } from "@wss/error-tracking";
import { plusSubscriptionProvider } from "~/my-account/Plus/plusSubscriptionProvider";

export type PlusSignupEndpointResponse = {
  wasCredited: boolean;
};

export const submitNewSubscriptionEnrollmentRequest = async (
  modalState: ModalState,
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>,
  plusPotentialSavingsInfo: PlusPotentialSavingsInfo | undefined,
  isOpenFromAd: boolean,
  handleShowWelcome: () => void,
  tokenId = 0,
): Promise<Throwable> => {
  const enrollRequest = new FormData();
  enrollRequest.append("savedCardId", tokenId.toString());
  enrollRequest.append("useTransactionsApi", "true");
  enrollRequest.append("shippingId", modalState.shippingId.toString());
  enrollRequest.append(
    "promotionName",
    modalState.promotionName?.toString() ?? "",
  );
  enrollRequest.append("marketingOptIn", modalState.marketingOptIn.toString());
  enrollRequest.append("accessor", "");

  if (plusPotentialSavingsInfo?.encryptedOrderNumber) {
    enrollRequest.append(
      "plusPromotionEncryptedOrderNumber",
      plusPotentialSavingsInfo.encryptedOrderNumber,
    );
  }

  try {
    const result = await fetch("/plus/enroll/", {
      method: "POST",
      body: enrollRequest,
    });

    if (result.ok) {
      new PinterestApiEvent(
        "signup",
        new PinterestTagSignUpEvent("Webplus"),
      ).sendEvent();

      const responseJson = await result.json<PlusSignupEndpointResponse>();

      handleShowWelcome();

      let path = "";
      switch (true) {
        case responseJson.wasCredited:
          path = `${window.location.href.split("?")[0]}?enrolled=1&wasCredited=${responseJson.wasCredited}`;
          break;
        case isOpenFromAd:
          path = window.location.href;
          break;
        default:
          path = `${window.location.href.split("?")[0]}?enrolled=1`;
          break;
      }
      setModalState((prev) => ({ ...prev, onCloseUrlUpdate: path }));
    } else {
      setModalState((prev) => ({
        ...prev,
        subscribeError: true,
        submitting: false,
      }));
    }

    return {
      isError: false,
    };
  } catch (e) {
    const error = new Error(`Error sending Plus enrollment to /plus/enroll/`, {
      cause: toError(e),
    });

    setModalState((prev) => ({
      ...prev,
      subscribeError: true,
      submitting: false,
    }));

    await logException(error);

    return {
      error,
      isError: true,
    };
  }
};

export const submitAddSubscriptionRequest = async (
  shippingId: number,
  modalState: ModalState,
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>,
  userGuid: string | undefined,
  handleShowWelcome: () => void,
  tokenId = 0,
): Promise<Throwable> => {
  try {
    const result = await plusSubscriptionProvider().addSubscriptions(
      userGuid ?? "",
      [
        {
          shippingAddressId: shippingId,
          paymentTokenId: tokenId,
          promotionName: modalState.promotionName ?? null,
        },
      ],
    );

    if (!result.error) {
      handleShowWelcome();
      setModalState((prev) => ({
        ...prev,
        onCloseUrlUpdate: `${window.location.href.split("?")[0]}?enrolled=1`,
      }));
    } else {
      setModalState((prev) => ({
        ...prev,
        subscribeError: true,
        submitting: false,
        subscribeCustomErrorMessage: result.error ?? "",
      }));
    }

    return {
      isError: false,
    };
  } catch (e) {
    const error = new Error(`Error sending additional Plus signup`, {
      cause: toError(e),
    });

    setModalState((prev) => ({
      ...prev,
      subscribeError: true,
      submitting: false,
    }));

    await logException(error);

    return {
      error,
      isError: true,
    };
  }
};

export const submitCancelledSubscriptionReinstateRequest = async (
  modalState: ModalState,
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>,
  plusPotentialSavingsInfo: PlusPotentialSavingsInfo | undefined,
  isOpenFromAd: boolean,
  handleShowWelcome: () => void,
): Promise<Throwable> => {
  const enrollRequest = new FormData();
  enrollRequest.append("useTransactionsApi", "true");
  enrollRequest.append("shippingId", modalState.shippingId.toString());
  enrollRequest.append("reinstateCancelledSubscription", "true");

  if (plusPotentialSavingsInfo?.encryptedOrderNumber) {
    enrollRequest.append(
      "plusPromotionEncryptedOrderNumber",
      plusPotentialSavingsInfo.encryptedOrderNumber,
    );
  }

  try {
    const result = await fetch("/plus/enroll/", {
      method: "POST",
      body: enrollRequest,
    });

    if (result.ok) {
      new PinterestApiEvent(
        "signup",
        new PinterestTagSignUpEvent("Webplus"),
      ).sendEvent();

      const responseJson = await result.json<PlusSignupEndpointResponse>();

      handleShowWelcome();

      let path = "";
      switch (true) {
        case responseJson.wasCredited:
          path = `${window.location.href.split("?")[0]}?enrolled=1&wasCredited=${responseJson.wasCredited}`;
          break;
        case isOpenFromAd:
          path = window.location.href;
          break;
        default:
          path = `${window.location.href.split("?")[0]}?enrolled=1`;
          break;
      }
      setModalState((prev) => ({ ...prev, onCloseUrlUpdate: path }));
    } else {
      setModalState((prev) => ({
        ...prev,
        subscribeError: true,
        submitting: false,
      }));
    }

    return {
      isError: false,
    };
  } catch (e) {
    const error = new Error(`Error sending Plus enrollment to /plus/enroll/`, {
      cause: toError(e),
    });
    setModalState((prev) => ({
      ...prev,
      subscribeError: true,
      submitting: false,
    }));
    await logException(error);

    return {
      error,
      isError: true,
    };
  }
};
